/*
 * Copyright (c) 2024 SARL SIGMA POLYNESIA
 */

import { SetStateAction, useCallback, useMemo } from 'react'

type UseSelectionOptions<T> = {
  allowDuplicates?: boolean
  items: T[]
  multiple?: boolean
  onChange (value: SetStateAction<T[]>): void
  source: T[]
}

export type UseSelectionHook<T> = {
  add (...values: T[]): void
  clear (): void
  empty: boolean
  includes (value: T): boolean
  items: T[]
  multiple: boolean
  remove (...values: T[]): void
  source: T[]
  toggle (...values: T[]): void
  toggleAll (): void
}

function useSelection<T> (options: UseSelectionOptions<T>): UseSelectionHook<T> {
  const {
    allowDuplicates = false,
    items,
    multiple = false,
    onChange,
    source
  } = options

  const add = useCallback((...values: T[]) => {
    onChange((s) => (
      allowDuplicates
        ? s.concat(values)
        : s.concat(values.filter((elm) => s.indexOf(elm) === -1))
    ))
  }, [allowDuplicates, onChange])

  const clear = useCallback(() => {
    onChange([])
  }, [onChange])

  const includes = useCallback((item: T): boolean => (
    items.indexOf(item) !== -1
  ), [items])

  const remove = useCallback((...values: T[]) => {
    onChange((s) => [].concat(s.filter((elm) => values.indexOf(elm) === -1)))
  }, [onChange])

  const toggle = useCallback((...values: T[]) => {
    onChange((s) => {
      let result: T[]

      if (multiple) {
        result = [].concat(s)

        values.forEach((elm) => {
          const index = s.indexOf(elm)
          if (index === -1) {
            result.push(elm)
          } else {
            result.splice(index, 1)
          }
        })
      } else if (values.length === 1) {
        result = s.indexOf(values[0]) === -1 ? [values[0]] : []
      } else {
        throw new Error('Cannot toggle more than one item when multiple option is set to false')
      }
      return result
    })
  }, [multiple, onChange])

  const toggleAll = useCallback(() => {
    if (items.length < source.length) {
      add(...source)
    } else {
      remove(...items)
    }
  }, [add, items, remove, source])

  return useMemo(() => ({
    add,
    clear,
    empty: items.length === 0,
    includes,
    items,
    multiple,
    remove,
    source,
    toggle,
    toggleAll
  }), [add, clear, source, includes, items, multiple, remove, toggle, toggleAll])
}

export default useSelection
